import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import { RequireModule, PageHeader, formatInr, downloadCsv } from './shared'

function displayName(user) {
  if (!user) return 'User'
  const combined = `${user.first_name || ''} ${user.last_name || ''}`.trim()
  return user.name || combined || 'User'
}

function isPaidRow(row) {
  return (
    String(row?.is_payment_success || '').toUpperCase() === 'YES' ||
    String(row?.payment_status || '').toLowerCase() === 'paid'
  )
}

function parseLedgerTime(row) {
  const raw = row?.date_added
  if (raw) {
    const t = Date.parse(raw)
    if (!Number.isNaN(t)) return t
  }
  const s = String(row?.created_at || '')
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/)
  if (m) {
    return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime()
  }
  return 0
}

function amountOf(row) {
  const n = Number(row?.payment_amount)
  return Number.isNaN(n) ? 0 : n
}

function particularsOf(row) {
  const type = row?.type_name || row?.donation_type_name || 'Donation'
  const mode = row?.mode_of_payment || row?.payment_mode || ''
  const paid = isPaidRow(row)
  const prefix = paid ? '' : 'Due — '
  return prefix + (mode ? `${type} (${mode})` : type)
}

function UserLedgerPage() {
  const { userId } = useParams()
  const location = useLocation()
  const stateUser = location.state?.user

  const [user, setUser] = useState(
    stateUser && String(stateUser.id) === String(userId) ? stateUser : null
  )
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const fromState = location.state?.user
    const hasStateUser = fromState && String(fromState.id) === String(userId)

    setLoading(true)
    setError('')
    setRows([])
    setUser(hasStateUser ? fromState : null)

    ;(async () => {
      const [paidData, unpaidData, userData] = await Promise.all([
        GetAction('get', { user_id: userId, status: 'paid' }, 'donation/DonationHistory'),
        GetAction('get', { user_id: userId, status: 'unpaid' }, 'donation/DonationHistory'),
        hasStateUser
          ? Promise.resolve(null)
          : PostAction('post', { id: userId }, 'user/userDetails'),
      ])

      if (cancelled) return

      if (!hasStateUser) {
        if (userData?.status === 'success' && userData.response) {
          setUser(userData.response)
        } else {
          setUser(null)
          setError(userData?.message || 'User not found.')
        }
      }

      const paidOk = paidData?.status === 'success' && Array.isArray(paidData.response)
      const unpaidOk = unpaidData?.status === 'success' && Array.isArray(unpaidData.response)
      if (!paidOk && !unpaidOk) {
        setRows([])
        setError((prev) => prev || paidData?.message || unpaidData?.message || 'Failed to load ledger.')
      } else {
        const paid = paidOk ? paidData.response : []
        const unpaid = unpaidOk ? unpaidData.response : []
        setRows([...paid, ...unpaid])
      }

      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [userId, location.state])

  const ledger = useMemo(() => {
    const sorted = [...rows].sort((a, b) => parseLedgerTime(a) - parseLedgerTime(b))
    let running = 0
    return sorted.map((row, i) => {
      const paid = isPaidRow(row)
      const amount = amountOf(row)
      const credit = paid ? amount : 0
      const debit = paid ? 0 : amount
      running += credit - debit
      return {
        ...row,
        index: i + 1,
        particulars: particularsOf(row),
        debit,
        credit,
        balance: running,
        ledger_status: paid ? 'Paid' : 'Pending',
      }
    })
  }, [rows])

  const totals = useMemo(
    () =>
      ledger.reduce(
        (acc, row) => ({
          debit: acc.debit + row.debit,
          credit: acc.credit + row.credit,
        }),
        { debit: 0, credit: 0 }
      ),
    [ledger]
  )
  const closing = totals.credit - totals.debit
  const name = displayName(user)

  const exportRows = () => {
    downloadCsv(
      `ledger-${String(name).replace(/\s+/g, '-').toLowerCase() || userId}.csv`,
      [
        { key: 'index', label: '#' },
        { key: 'created_at', label: 'Date' },
        { key: 'particulars', label: 'Particulars' },
        { key: 'debit', label: 'Debit' },
        { key: 'credit', label: 'Credit' },
        { key: 'balance', label: 'Balance' },
        { key: 'ledger_status', label: 'Status' },
      ],
      ledger
    )
  }

  return (
    <RequireModule moduleKey="user_reports">
      <PageHeader
        title={`${name} — Ledger`}
        subtitle="Donation ledger: pending amounts as debit, paid amounts as credit."
        actions={
          <>
            <Link to="/manage/user-reports" className="btn btn-sm btn-outline-secondary me-2">
              Back to User Reports
            </Link>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={exportRows}
              disabled={!ledger.length}
            >
              Export CSV
            </button>
          </>
        }
      />
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      {user ? (
        <div className="manage-card mb-3">
          <div className="row">
            <div className="col-md-3 mb-2">
              <div className="text-muted small">Name</div>
              <div>{name}</div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="text-muted small">Mobile</div>
              <div>{user.mobile || '—'}</div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="text-muted small">City</div>
              <div>{user.city || '—'}</div>
            </div>
            <div className="col-md-3 mb-2">
              <div className="text-muted small">Email</div>
              <div>{user.email_address || '—'}</div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="row mb-3">
        <div className="col-md-4 mb-2">
          <div className="manage-stat">
            <div className="manage-stat-label">Total Debit (Pending)</div>
            <div className="manage-stat-value">{formatInr(totals.debit)}</div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="manage-stat">
            <div className="manage-stat-label">Total Credit (Paid)</div>
            <div className="manage-stat-value">{formatInr(totals.credit)}</div>
          </div>
        </div>
        <div className="col-md-4 mb-2">
          <div className="manage-stat">
            <div className="manage-stat-label">Closing Balance</div>
            <div className="manage-stat-value">{formatInr(closing)}</div>
          </div>
        </div>
      </div>

      <div className="manage-card">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle manage-table manage-ledger">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>#</th>
                  <th>Date</th>
                  <th>Particulars</th>
                  <th className="num">Debit</th>
                  <th className="num">Credit</th>
                  <th className="num">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No donations found for this user.
                    </td>
                  </tr>
                ) : (
                  ledger.map((row) => (
                    <tr key={row.payment_detail_id || row.payment_id || row.index}>
                      <td>{row.index}</td>
                      <td>{row.created_at || '—'}</td>
                      <td>{row.particulars}</td>
                      <td className="num">{row.debit ? formatInr(row.debit) : '—'}</td>
                      <td className="num">{row.credit ? formatInr(row.credit) : '—'}</td>
                      <td className="num">{formatInr(row.balance)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {ledger.length > 0 ? (
                <tfoot>
                  <tr>
                    <td colSpan={3}>Total</td>
                    <td className="num">{formatInr(totals.debit)}</td>
                    <td className="num">{formatInr(totals.credit)}</td>
                    <td className="num">{formatInr(closing)}</td>
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        )}
        {!loading ? (
          <div className="text-muted small">{ledger.length} record(s)</div>
        ) : null}
      </div>
    </RequireModule>
  )
}

export default UserLedgerPage
